/** analysis.hooks */

import { GetJobStatus } from '@/interface/analysis.interface';
import { PageRequest, PageResponse } from '@/interface/index.interface';
import analysisService from '@/services/analysis.services';
import { definitions } from '@/types/api';
import { IJobModuleQuestionParse, IJobModuleQuestionParseStu } from '@/views/Teacher/addJob/js/interface';
import { parseData, parseDataStu } from '@/views/Teacher/addJob/question/js';
import { QuestionI, StuParseQuestionI } from '@/views/Teacher/addJob/question/js/interface';
import { TJobStatus, TJobStatusArr } from '@/views/Teacher/caseAnalysis/js/interface';

/** 实验列表table-column */
const analysisColumns = [
  { title: '作业名称', dataIndex: 'assignmentName' },
  { title: '模式', dataIndex: 'assignmentModel', slots: { customRender: 'assignmentModel' } },
  { title: '开始时间', dataIndex: 'beginTime' },
  { title: '结束时间', dataIndex: 'endTime' },
  { title: '作业状态', dataIndex: 'jobStatus', slots: { customRender: 'jobStatus' } },
  { title: '分数', dataIndex: 'score', slots: { customRender: 'score' } },
  { title: '排名', dataIndex: 'ranking', slots: { customRender: 'ranking' } },
  { title: '操作', dataIndex: 'action', slots: { customRender: 'action' } },
];

/** 列表 */
export interface TableData {
  tableLoading: boolean;
  tableList: definitions['Experiment'][];
  columns: typeof analysisColumns;
}
export interface TableDataAnalysis {
  tableLoadingAnalysis: boolean;
  tableListAnalysis: definitions['Experiment'][];
  columnsAnalysis: typeof analysisColumns;
}
/** listQuestionStuAnswer-option */
export interface ListQuestionStuAnswer {
  userId?: string;
}
/** status转状态 */
export const getJobStatus = (data: GetJobStatus): TJobStatus => TJobStatusArr[data.assignStatus];

/** 案例分析hooks */
const useAnalysisHooks = () => {
  const {
    listAssignHomeworksByUserId, listQuestionModuleByJobId, listQuestionByModuleId, listStuQuestionAnswer, saveStuAnswerApi,
  } = analysisService();

  /**
   * 发布状态
   * 发布后教师不能评分
   */
  const currentAssignmentPublish = {
    get: () => sessionStorage.getItem('currentAssignmentPublish'),
    set: (val: '1' | '0') => {
      sessionStorage.setItem('currentAssignmentPublish', val);
    },
  };

  /**
   * 教师端
   * 记录 创建作业的步骤
   */
  const currentTeacherAssignmentStep = {
    get: () => sessionStorage.getItem('currentTeacherAssignmentStep') || '',
    set: (status: string) => {
      sessionStorage.setItem('currentTeacherAssignmentStep', status);
    },
    remove: () => {
      sessionStorage.removeItem('currentTeacherAssignmentStep');
    },
  };

  /** 学生端-根据userId查询作业列表页 */
  const listAssignHomeworkList = async (param: PageRequest<{
    assignStatus?: number;
  }>): Promise<PageResponse<definitions['AssignHomework']>> => {
    const { list, totalRecords } = await listAssignHomeworksByUserId(param);
    return {
      list: list || [],
      total: totalRecords || 0,
    };
  };

  /** 学生端-根据jobId获得所有题目 */
  const listQuestionDataByJobId = async (jobId: string) => {
    const moduleList = await listQuestionModuleByJobId(jobId);
    const actions = moduleList.map((mo) => new Promise<definitions['CustomQuestion'][]>((resolve) => {
      listQuestionByModuleId(mo.id!).then((questionList) => {
        resolve(questionList);
      });
    }));
    const allQuestion = await Promise.all(actions);
    const resData = moduleList.map((mo, moI) => {
      const questionList = allQuestion[moI].map((qu) => parseData(qu as QuestionI));
      return { ...mo, jobModuleName: mo.moduleName, questionList };
    });
    return resData as IJobModuleQuestionParse[];
  };

  /**
   * 匹配做过的题目
   * 学生端不用传userId
   * 教师端根据userId查指定学生
   */
  const listQuestionStuAnswer = async (list: IJobModuleQuestionParse[], assignmentId: string, opt: ListQuestionStuAnswer = {}): Promise<IJobModuleQuestionParseStu[]> => {
    const { userId } = opt;
    const stuAnswerList = await listStuQuestionAnswer(assignmentId, userId);
    const resList = list.map((mo) => {
      const questionList = mo.questionList.map((qu) => {
        const findQuestion = (qInfo: definitions['StuQuestionAnswer']) => qInfo.questionId === qu.id;
        const stuInfo = stuAnswerList.find(findQuestion);
        let resInfo: StuParseQuestionI = { ...qu };
        if (stuInfo) {
          resInfo = {
            ...resInfo,
            stuAnswer: stuInfo.stuAnswer,
            stuScore: stuInfo.score,
            stuTrue: stuInfo.score === qu.score, // 如果得分和满分相等，表示做对了-填空题没有对错
            blankNumber: stuInfo.gradingState ? 1 : 0, // true表示已经评分，false或undefined 未评分
          };
        }
        return parseDataStu(resInfo);
      });
      return { ...mo, questionList };
    });

    return resList;
  };

  /** 学生端-做题 */
  const saveStuAnswer = async (assignmentId: string, questionId: string, questionCount: number, stuAnswer: string) => {
    saveStuAnswerApi({
      assignmentId, questionId, questionCount, stuAnswer,
    });
  };

  return {
    currentAssignmentPublish,
    currentTeacherAssignmentStep,
    listQuestionDataByJobId,
    listAssignHomeworkList,
    listQuestionStuAnswer,
    saveStuAnswer,
    analysisColumns,
  };
};

export default useAnalysisHooks;
